import { useState } from 'react';
import { FiBarChart2, FiCalendar } from 'react-icons/fi';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { analyticsApi } from '../services/api';
import AssignmentsByOwner from './analytics/AssignmentsByOwner';
import ExecutiveSummary from './analytics/ExecutiveSummary';
import RecentDistributionsReport from './analytics/RecentDistributionsReport';

export default function Analytics() {
  const { selectedPortal } = useHubSpotPortals();
  const [objectType, setObjectType] = useState('contact');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportType, setReportType] = useState('assignments_by_owner');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [buildStatus, setBuildStatus] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const objectTypes = [
    { value: 'contact', label: 'Contact' },
    { value: 'company', label: 'Company' },
    { value: 'deal', label: 'Deal' },
    { value: 'lead', label: 'Lead' },
    { value: 'ticket', label: 'Ticket' },
  ];

  const reportTypes = [
    { value: 'assignments_by_owner', label: 'Assignments by Owner' },
    { value: 'executive_summary', label: 'Executive Summary' },
    { value: 'recent_distributions', label: 'Recent Distributions' },
  ];

  const fetchRecentDistributions = async (page, selectedPageSize = pageSize) => {
    const startISO = new Date(startDate + 'T00:00:00').toISOString();
    const endISO = new Date(endDate + 'T23:59:59.999').toISOString();

    const result = await analyticsApi.queryRecentDistributions({
      portalId: selectedPortal.portal_id,
      objectType: objectType || null,
      startISO,
      endISO,
      page,
      limit: selectedPageSize,
    });

    if (result.statusCode === 202 || result.data?.status === 'building') {
      setBuildStatus('building');
      return false;
    }

    setBuildStatus(null);
    setCurrentPage(page);
    setAnalyticsData(result.data);
    return true;
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleViewAnalytics = async () => {
    if (!selectedPortal?.portal_id) {
      setError('No active portal selected.');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }

    console.log('Analytics Viewing...');

    setLoading(true);
    setError(null);
    setAnalyticsData(null);
    setBuildStatus(null);
    setCurrentPage(1);

    try {
      if (reportType === 'recent_distributions') {
        const maxPollAttempts = 15;
        let attempt = 0;
        let done = false;
        while (!done && attempt < maxPollAttempts) {
          done = await fetchRecentDistributions(1, pageSize);
          if (!done) {
            attempt += 1;
            await wait(2000);
          }
        }
        if (!done) {
          setError('Report is still being generated. Please try again in a few seconds.');
        }
      } else {
        // Convert local dates to UTC ISO strings
        const startISO = new Date(startDate + 'T00:00:00').toISOString();
        const endISO = new Date(endDate + 'T23:59:59.999').toISOString();

        const data = await analyticsApi.queryAnalytics({
          portalId: selectedPortal.portal_id,
          objectType: objectType || null,
          startISO,
          endISO,
          reportType,
        });

        setAnalyticsData(data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousPage = async () => {
    if (currentPage <= 1 || loading) return;
    setLoading(true);
    setError(null);
    try {
      await fetchRecentDistributions(currentPage - 1);
    } catch {
      setError('Failed to load previous page.');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await fetchRecentDistributions(currentPage + 1);
    } catch {
      setError('Failed to load next page.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = async (size) => {
    setPageSize(size);
    if (reportType !== 'recent_distributions' || !analyticsData) return;
    setLoading(true);
    setError(null);
    try {
      await fetchRecentDistributions(1, size);
    } catch {
      setError('Failed to reload report with new page size.');
    } finally {
      setLoading(false);
    }
  };

  const escapeCsvValue = (value) => {
    const stringValue = value == null ? '' : String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadCsv = (filename, content) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportRecentDistributionsCsv = async () => {
    if (!selectedPortal?.portal_id) {
      setError('No active portal selected.');
      return;
    }
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }

    setExportingCsv(true);
    setError(null);
    try {
      const startISO = new Date(startDate + 'T00:00:00').toISOString();
      const endISO = new Date(endDate + 'T23:59:59.999').toISOString();
      const exportLimit = 250;
      let page = 1;
      const allRows = [];
      let totalPages = null;

      while (totalPages == null || page <= totalPages) {
        const result = await analyticsApi.queryRecentDistributions({
          portalId: selectedPortal.portal_id,
          objectType: objectType || null,
          startISO,
          endISO,
          page,
          limit: exportLimit,
        });

        if (result.statusCode === 202 || result.data?.status === 'building') {
          setError('Report is still being generated. Please try export again in a few seconds.');
          return;
        }

        const pageRows = result.data?.rows || [];
        allRows.push(...pageRows);
        if (totalPages == null) {
          const apiTotalPages = Number(result.data?.totalPages);
          const apiTotalRows = Number(result.data?.totalRows);
          if (Number.isFinite(apiTotalPages) && apiTotalPages > 0) {
            totalPages = apiTotalPages;
          } else if (Number.isFinite(apiTotalRows) && apiTotalRows >= 0) {
            totalPages = Math.max(1, Math.ceil(apiTotalRows / exportLimit));
          } else {
            totalPages = result.data?.hasMore ? page + 1 : page;
          }
        } else if (result.data?.hasMore && page >= totalPages) {
          totalPages += 1;
        }

        if (!result.data?.hasMore && page >= totalPages) {
          break;
        }
        page += 1;
      }

      const headers = [
        'hs_object_type',
        'hs_object_id',
        'team_name',
        'assignee_name',
        'error_name',
        'timestamp',
      ];
      const lines = [headers.join(',')];
      for (const row of allRows) {
        lines.push(
          [
            escapeCsvValue(row.objectType || ''),
            escapeCsvValue(row.objectId || ''),
            escapeCsvValue(row.teamName || ''),
            escapeCsvValue(row.assigneeName || ''),
            escapeCsvValue(row.errorName || ''),
            escapeCsvValue(row.timestamp || ''),
          ].join(',')
        );
      }

      downloadCsv(`recent-distributions-${selectedPortal.portal_id}.csv`, `${lines.join('\n')}\n`);
    } catch (err) {
      console.error('Error exporting recent distributions CSV:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Distribution Analytics</h1>
            {selectedPortal && (
              <p className="text-sm text-blue-700 mt-1">
                Showing analytics for portal: 
                <span className="font-semibold"> {selectedPortal.hub_domain}</span> 
                <span className="ml-1 text-gray-600">(ID: {selectedPortal.portal_id})</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FiBarChart2 className="w-5 h-5 mr-2" />
          Filters
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Object Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Object Type
            </label>
            <select
              value={objectType}
              onChange={(e) => setObjectType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              {objectTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline w-4 h-4 mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline w-4 h-4 mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {reportTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* View Analytics Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleViewAnalytics}
            disabled={loading || !startDate || !endDate}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <FiBarChart2 className="w-4 h-4" />
                <span>View Analytics</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {buildStatus === 'building' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-900">
            Building cached report. This can take a few seconds for large datasets.
          </p>
        </div>
      )}

      {/* Analytics Results */}
      {analyticsData && (
        <div className="space-y-6">
          {reportType === 'assignments_by_owner' && (
            <AssignmentsByOwner data={analyticsData} />
          )}
          {reportType === 'executive_summary' && (
            <ExecutiveSummary data={analyticsData} />
          )}
          {reportType === 'recent_distributions' && (
            <RecentDistributionsReport
              data={analyticsData}
              page={currentPage}
              pageSize={pageSize}
              onPrevious={handlePreviousPage}
              onNext={handleNextPage}
              onPageSizeChange={handlePageSizeChange}
              onExportCsv={handleExportRecentDistributionsCsv}
              exporting={exportingCsv}
              loading={loading}
            />
          )}
          {analyticsData.topErrors && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Error Details</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Showing the most frequent error messages in this period.
                </p>
              </div>
              <div className="p-6">
                {analyticsData.topErrors.length === 0 ? (
                  <div className="text-gray-500">No errors found for this period.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Error
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {analyticsData.topErrors.map(([errorMessage, count]) => (
                          <tr key={errorMessage} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {errorMessage}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && !analyticsData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FiBarChart2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Yet</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Select your filters and click "View Analytics" to see distribution metrics.
          </p>
        </div>
      )}
    </div>
  );
}
